import { Link } from "react-router-dom";
import { Sparkles, Flower2, Gem, Heart } from "lucide-react";
import { products } from "@/data/products";

// Use catalog imagery only — no third-party photography.
const forest = products.find((p) => p.slug === "forest-whisper-earrings");
const sunfall = products.find((p) => p.slug === "sunfall-earrings");
const twilight = products.find((p) => p.slug === "twilight-bloom-earrings");
const amber = products.find((p) => p.slug === "amber-pearl-earrings");

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#FAF8F5]">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 pt-14 md:pt-24 pb-14 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="overline">Our Story</p>
            <h1 className="mt-5 md:mt-6 font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#2A2626]">
              Made slowly.<br />
              <span className="italic font-light">Worn quietly.</span>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16px] leading-[1.9] text-[#5C564F] max-w-md">
              Innayaa was born from a love for stones — the way they gather
              light, hold stories, and shift with the wearer. Every piece is
              handcrafted in small batches with semi-precious crystals,
              freshwater pearls and 925 sterling silver ear hooks.
            </p>
          </div>
        </div>
      </section>

      {/* Editorial image band using catalog lifestyle shot */}
      <section className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-10 lg:px-16">
        <div className="w-full aspect-[16/9] md:aspect-[16/8] overflow-hidden bg-[#F0EBE1]">
          <img src={forest.images.lifestyle} alt="Innayaa lifestyle" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Manifesto */}
      <section className="max-w-[1000px] mx-auto px-5 sm:px-6 md:px-10 py-16 md:py-24 lg:py-32 text-center">
        <p className="overline">The Innayaa Belief</p>
        <blockquote className="mt-6 md:mt-8 font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#2A2626]">
          &ldquo;Modern crystals, handcrafted with intention.<br />
          <span className="italic">Beauty you can feel, not just see.</span>&rdquo;
        </blockquote>
      </section>

      {/* Values */}
      <section className="bg-[#F0EBE1]/70 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {[
              { Icon: Gem, title: "Genuine stones", text: "Every stone is semi-precious — sourced with care, chosen for its natural light and character." },
              { Icon: Flower2, title: "Handcrafted", text: "Individually made in small batches. No factory-line finish, no two pairs exactly alike." },
              { Icon: Sparkles, title: "925 silver", text: "Ear hooks in 925 sterling silver for skin-friendly, everyday wear." },
              { Icon: Heart, title: "Made to last", text: "Built for years of gentle wearing, with care — designed to become an heirloom." },
            ].map(({ Icon, title, text }) => (
              <div key={title} className="border-t border-[#DCD5CA] pt-6">
                <Icon strokeWidth={1.25} size={24} className="text-[#2A2626]" />
                <h3 className="mt-4 font-serif text-2xl text-[#2A2626]">{title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#5C564F]">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <img src={twilight.images.lifestyle} alt="Twilight Bloom worn" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:col-span-6 lg:pl-8">
            <p className="overline">The Process</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#2A2626] leading-tight">
              Stone. Wire.<br />
              <span className="italic">Patience.</span>
            </h2>
            <ol className="mt-8 md:mt-10 space-y-6 md:space-y-8">
              {[
                { n: "01", title: "Chosen by hand", text: "Each stone is selected in person — for colour, cut, and the way it holds light." },
                { n: "02", title: "Wired individually", text: "Delicate silver-tone wiring and chain assembly, one piece at a time." },
                { n: "03", title: "Finished in 925 silver", text: "Every pair is finished with 925 sterling silver ear hooks, ready to travel with you." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[64px_1fr] md:grid-cols-[80px_1fr] gap-4 md:gap-5 border-t border-[#DCD5CA] pt-6">
                  <span className="font-serif italic text-2xl md:text-3xl text-[#B89B7E]">{s.n}</span>
                  <div>
                    <h3 className="font-serif text-xl text-[#2A2626]">{s.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#5C564F]">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Editorial trio using lifestyle imagery */}
      <section className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          <div className="aspect-[3/4] overflow-hidden bg-[#F0EBE1]">
            <img src={sunfall.images.lifestyle} alt="Sunfall Earrings" className="w-full h-full object-cover" />
          </div>
          <div className="aspect-[3/4] overflow-hidden bg-[#F0EBE1] mt-6 md:mt-10">
            <img src={amber.images.lifestyle} alt="Amber Pearl Earrings" className="w-full h-full object-cover" />
          </div>
          <div className="aspect-[3/4] overflow-hidden bg-[#F0EBE1]">
            <img src={forest.images.closeUp} alt="Forest Whisper detail" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#DCD5CA] py-16 md:py-24 text-center px-5">
        <p className="overline">Begin your Innayaa</p>
        <h2 className="mt-5 md:mt-6 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#2A2626]">
          Find the pair that speaks to you.
        </h2>
        <Link
          to="/shop"
          data-testid="about-shop-cta"
          className="mt-8 md:mt-10 inline-block bg-[#2A2626] text-[#FAF8F5] px-8 md:px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
        >
          Explore the collection
        </Link>
      </section>
    </div>
  );
}
